import { useEffect } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'

import Box from '@mui/material/Box'
import FormHelperText from '@mui/material/FormHelperText'
import { alpha } from '@mui/material/styles'

import BubbleMenu from './BubbleMenu'
import { EXTENSIONS } from './extensions'
import { prepareLegacyHtml } from './legacy'
import Toolbar from './Toolbar'

export default function Editor({ value, onChange, onBlur, editable = true, error }) {
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: parseContent(value),
    editable,
    // Client-only SPA: render synchronously and don't re-render the editor host on every
    // transaction — the toolbar/bubble subscribe to the slices they need via useEditorState.
    immediatelyRender: true,
    shouldRerenderOnTransaction: false,
    onUpdate: ({ editor }) => onChange?.(editor.isEmpty ? '' : JSON.stringify(editor.getJSON())),
    onBlur: () => onBlur?.(),
  })

  useEffect(() => {
    editor?.setEditable(editable)
  }, [editor, editable])

  return (
    <Box>
      {editable && editor && <Toolbar editor={editor} />}
      {editable && editor && <BubbleMenu editor={editor} />}
      <Box sx={styles.body}>
        <EditorContent editor={editor} />
      </Box>
      {error && <FormHelperText error>{error}</FormHelperText>}
    </Box>
  )
}

const EMPTY_DOC = { type: 'doc', content: [{ type: 'paragraph' }] }

function parseContent(value) {
  if (!value) return EMPTY_DOC
  try {
    const doc = JSON.parse(value)
    if (doc?.type === 'doc') return doc
    return EMPTY_DOC
  } catch {
    // Legacy v1 content is Quill HTML, not JSON. Preprocess Quill-specific markup (ql-align-*
    // classes → inline text-align) then hand the HTML to Tiptap, which parses it through the
    // extensions' parseHTML rules (including the Video node's legacy <youtube>/<vimeo> tags).
    // Editing then re-serializes as v2 JSON on save — a one-way, edit-triggered migration.
    return prepareLegacyHtml(value)
  }
}

// Styling for the ProseMirror-rendered content (applies in both edit and read-only modes).
const styles = {
  body: (theme) => ({
    '& .tiptap': {
      outline: 'none',
      minHeight: 500,
    },
    // Highlighter color — an intentional content color with no theme equivalent.
    '& .tiptap mark': {
      backgroundColor: alpha(theme.palette.warning.main, 0.35),
      color: theme.palette.text.primary,
    },
    '& .tiptap a': {
      color: theme.palette.primary.main,
      textDecoration: 'none',
      borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`,
      cursor: 'pointer',
    },
    '& .tiptap a:hover': {
      borderBottomColor: theme.palette.primary.main,
    },
    '& .tiptap :is(ul, ol)': {
      margin: 0,
      paddingLeft: '1.5em',
    },
    '& .tiptap li': {
      margin: '0.3em 0',
    },
    '& .tiptap li > p': {
      margin: 0,
    },
    '& .tiptap :is(ul, ol) :is(ul, ol)': {
      marginTop: '0.3em',
    },
    '& .tiptap blockquote': {
      margin: '4px 0',
      paddingLeft: '8px',
      borderLeft: `3px solid ${theme.palette.primary.main}`,
    },
    '& .tiptap pre': {
      margin: '8px 0',
      padding: '8px 12px',
      borderRadius: '8px',
      backgroundColor: theme.palette.grey[100],
    },
    '& .tiptap img': {
      display: 'block',
      maxWidth: '100%',
      height: 'auto',
      borderRadius: '8px',
      margin: '1.1em 0',
    },
    // Selected atom nodes (image, video).
    '& .tiptap .ProseMirror-selectednode': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '2px',
      borderRadius: '8px',
    },
    '& .tiptap hr': {
      border: 'none',
      borderTop: `1px solid ${theme.palette.divider}`,
      margin: '1.5em 0',
    },
    '& .tiptap ::selection': {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
    },
    // Placeholder prompt for the empty editor (pairs with the Placeholder extension).
    '& .tiptap p.is-editor-empty:first-child::before': {
      content: 'attr(data-placeholder)',
      color: theme.palette.text.disabled,
      float: 'left',
      height: 0,
      pointerEvents: 'none',
    },
  }),
}
